import { useCallback, useEffect, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { BlockComponents } from '@winnin/winnin_ui.components.block-components'
import { Button } from '@winnin/winnin_ui.components.button'
import { Title } from '@winnin/winnin_ui.components.title'

import InputWithIcon from 'components/InputWithIcon'
import { JobFolderSelectorList } from 'components/JobFolderSelectorList'
import { T, useTranslate } from 'lib/tolgee'

import AddToJobFolderModalSkeleton from './components/AddToJobFolderModalSkeleton'

import './index.scss'

const TOLGEE_PREFFIX = 'AddToJobFolderModalContent'
const emptyJobsKey = `${TOLGEE_PREFFIX}-emptyJobs`
const emptySearchKey = `${TOLGEE_PREFFIX}-emptySearch`
const newJobButtonKey = `${TOLGEE_PREFFIX}-newJob-button`
const searcInputPlaceolderKey = `${TOLGEE_PREFFIX}-searchInput-placeholder`
const sectionTitleAllKey = `${TOLGEE_PREFFIX}-sectionTitle-all`

/**
 * @param {Object} props
 * @param {JobWithFolders[]} props.jobsList
 * @param {JobWithFolders} props.mostRecentJob
 * @param {boolean} [props.disableJobCreation]
 * @param {boolean} [props.isLoading]
 * @param {number} [props.jobsCount]
 * @param {number} [props.jobsLimit]
 * @param {number} [props.foldersLimit]
 * @param {string} [props.searchTerm]
 * @param {(searchTerm: string) => void} props.onSearchTermChange
 * @param {() => PromiseLike<void>} props.loadMoreJobs
 * @param {boolean} props.hasMoreJobs
 * @param {() => void} props.onClose
 * @param {(jobId: string, foldersIds: string[]) => PromiseLike<void>} props.onSave
 * @param {(jobId: string, foldersIds: string[]) => PromiseLike<void>} props.onClickNewJob
 */
export default function AddToJobFolderModalContent(props) {
  const {
    jobsList,
    disableJobCreation,
    jobsCount,
    jobsLimit,
    searchTerm,
    onSearchTermChange,
    loadMoreJobs,
    hasMoreJobs,
    foldersLimit,
    isLoading,
    isFetchingMore,
    onClose,
    onSave,
    onClickNewJob,
    mostRecentJob,
  } = props
  const t = useTranslate()

  const [selectedFolder, setSelectedFolder] = useState({ folderId: null, jobId: null })
  const [isSaving, setIsSaving] = useState(false)

  const filteredJobs = useMemo(() => {
    const jobsWithoutMostRecent = jobsList.filter(job => job.id !== mostRecentJob?.id)

    if (jobsList.length > 1) {
      return jobsWithoutMostRecent
    }

    return jobsList
  }, [jobsList, mostRecentJob])

  const handleSelectJobFolder = (jobId, folderId, shouldCollapse) => {
    setSelectedFolder(prev => {
      if (prev?.jobId === jobId && shouldCollapse) {
        return null
      }
      return { jobId, folderId }
    })
  }

  const handleSave = useCallback(async () => {
    try {
      if (selectedFolder) {
        setIsSaving(true)

        const jobId = selectedFolder?.jobId
        const foldersIds = selectedFolder?.folderId ? [selectedFolder.folderId] : []

        await onSave(jobId, foldersIds)
      }
    } finally {
      setIsSaving(false)
    }
  }, [onSave, selectedFolder])

  const getTolgeeKey = key => `${key}-asDashboard`

  const canSave = !isSaving && !!selectedFolder?.jobId

  useEffect(() => {
    if (selectedFolder?.jobId) {
      return
    }
    if (mostRecentJob?.id) {
      setSelectedFolder({ folderId: null, jobId: mostRecentJob?.id })
      return
    }
    const firstItem = jobsList?.[0] ?? {}
    if (firstItem?.id) {
      setSelectedFolder({ folderId: null, jobId: firstItem?.id })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsList, mostRecentJob])

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMoreJobs,
    onLoadMore: loadMoreJobs,
  })

  return (
    <div className="AddToJobFolderModal-wrapper">
      <div className="titleSection">
        <Title tag="h2">
          <T keyName={`${TOLGEE_PREFFIX}-modal-title`}>Salvar referências</T>
        </Title>
        <InputWithIcon
          onChange={e => onSearchTermChange(e.target.value)}
          value={searchTerm}
          name="searchInput"
          placeholder={t({
            key: getTolgeeKey(searcInputPlaceolderKey),
            defaultValue: 'Search for a dashboard',
          })}
          icon="search"
        />
      </div>

      <div className="jobsList" ref={rootRef}>
        {isLoading ? (
          <AddToJobFolderModalSkeleton />
        ) : (
          <>
            {!!filteredJobs.length && (
              <>
                {mostRecentJob && !searchTerm && jobsList.length > 1 ? (
                  <section className="sectionWrapper">
                    <h3 className="sectionTitle">
                      <T keyName={`${TOLGEE_PREFFIX}-sectionTitle-lastEdited`}>Editado por último por você</T>
                    </h3>
                    <JobFolderSelectorList
                      jobs={[mostRecentJob]}
                      selectedJobId={selectedFolder?.jobId}
                      foldersLimit={foldersLimit}
                      onSelectFolder={handleSelectJobFolder}
                    />
                  </section>
                ) : null}
                <section className="sectionWrapper">
                  <h3 className="sectionTitle">
                    <T keyName={getTolgeeKey(sectionTitleAllKey)}>{'Todos os dashboards'}</T>
                  </h3>
                  <JobFolderSelectorList
                    jobs={filteredJobs}
                    selectedJobId={selectedFolder?.jobId}
                    foldersLimit={foldersLimit}
                    onSelectFolder={handleSelectJobFolder}
                  />
                </section>
              </>
            )}

            {!filteredJobs.length && !searchTerm && (
              <div className="emptySection">
                <T
                  keyName={getTolgeeKey(emptyJobsKey)}
                  parameters={{
                    title: <Title tag="h3" weight="bold" />,
                    p: <p />,
                  }}
                >
                  {`
                    <title>
                      Start your first dashboard!
                    </title>
                    <p>
                      Create a dashboard to save content that will be reference for your content strategy so you
                      can revisit later.
                    </p>
                  `}
                </T>
              </div>
            )}

            {!filteredJobs.length && !!searchTerm && (
              <div className="emptySection">
                <T
                  keyName={getTolgeeKey(emptySearchKey)}
                  parameters={{
                    p: <p />,
                  }}
                >
                  {`
                    <p>
                      No dashboards were found, try searching for another name.
                    </p>
                  `}
                </T>
              </div>
            )}
            {isFetchingMore && <AddToJobFolderModalSkeleton />}
            {!isLoading && !isFetchingMore && hasMoreJobs && <div style={{ minHeight: 1 }} ref={sentryRef} />}
          </>
        )}
      </div>

      <div className="buttonsSection">
        <BlockComponents
          blockMessage={t({
            key: `${TOLGEE_PREFFIX}-newJob-block-message`,
            parameters: {
              jobsCount,
              jobsLimit,
            },
            defaultValue:
              'This workspace is using {jobsCount} out of {jobsLimit} available. If you are having problems with this limit, please reach out through our chat.',
          })}
          tooltipPositionClassname="tooltippedNe"
          isClickBlocked={disableJobCreation}
        >
          <Button
            icon="add"
            iconSize={16}
            appearance="link"
            variation="faded"
            disabled={isSaving || disableJobCreation}
            onClick={onClickNewJob}
            style={{ fontSize: '14px' }}
          >
            <T keyName={getTolgeeKey(newJobButtonKey)}>{'Novo dashboard'}</T>
          </Button>
        </BlockComponents>

        <div className="actionsButtons">
          <Button appearance="link" variation="faded" onClick={onClose} disabled={isSaving}>
            <T keyName={`${TOLGEE_PREFFIX}-cancel-button`}>Cancelar</T>
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            <T keyName={`${TOLGEE_PREFFIX}-save-button`}>Salvar</T>
          </Button>
        </div>
      </div>
    </div>
  )
}
